import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, Plus, Edit, Trash2, Eye, EyeOff,
  Upload, Loader2, ImageIcon, X, GripVertical, ExternalLink,
  Users as UsersIcon, CalendarDays, Link as LinkIcon, ClipboardList,
  Megaphone,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const emptyAd = {
  title: "",
  description: "",
  image_url: "",
  link_url: "",
  cta_label: "자세히 보기",
  cta_style: "light",
  action_type: "link",
  event_details: "",
  event_date: "",
  is_active: true,
  sort_order: 0,
};

const emptyRecAd = {
  tag: "",
  title: "",
  color_theme: "blue",
  link_url: "",
  is_active: true,
  sort_order: 0,
};

const REC_COLOR_OPTIONS = [
  { value: "blue",     label: "블루",     swatch: "bg-blue-500" },
  { value: "emerald",  label: "에메랄드", swatch: "bg-emerald-500" },
  { value: "amber",    label: "앰버",     swatch: "bg-amber-500" },
  { value: "rose",     label: "로즈",     swatch: "bg-rose-500" },
  { value: "violet",   label: "바이올렛", swatch: "bg-violet-500" },
  { value: "burgundy", label: "버건디",   swatch: "bg-burgundy-500" },
  { value: "sky",      label: "스카이",   swatch: "bg-sky-500" },
  { value: "teal",     label: "틸",       swatch: "bg-teal-500" },
  { value: "stone",    label: "스톤",     swatch: "bg-stone-500" },
];

const REC_TAG_PRESETS = ["여행", "건강식품", "골프", "뷰티", "패션", "맛집", "도서", "교육", "이벤트", "프리미엄"];

const REC_COLOR_TEXT = {
  blue:     { tag: "text-blue-500",     title: "text-blue-700" },
  emerald:  { tag: "text-emerald-500",  title: "text-emerald-700" },
  amber:    { tag: "text-amber-600",    title: "text-amber-800" },
  rose:     { tag: "text-rose-500",     title: "text-rose-700" },
  violet:   { tag: "text-violet-500",   title: "text-violet-700" },
  burgundy: { tag: "text-burgundy-500", title: "text-burgundy-700" },
  sky:      { tag: "text-sky-500",      title: "text-sky-700" },
  teal:     { tag: "text-teal-500",     title: "text-teal-700" },
  stone:    { tag: "text-stone-500",    title: "text-stone-800" },
};

const resolveImageUrl = (url) => {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  return `${API_URL}${url}`;
};

export default function AdminAdsPage() {
  const navigate = useNavigate();
  const { isAuthenticated, user, loading: authLoading, token } = useAuth();
  const [activeTab, setActiveTab] = useState("recommended"); // "event" | "recommended" — 기본: 사이드바와 같은 텍스트 카드 폼
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingAd, setEditingAd] = useState(null);
  const [form, setForm] = useState(emptyAd);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [applicationsAd, setApplicationsAd] = useState(null);
  const [applications, setApplications] = useState([]);
  const [loadingApps, setLoadingApps] = useState(false);

  // 추천 광고 (커뮤니티 우측 사이드바 텍스트 카드) 상태
  const [recAds, setRecAds] = useState([]);
  const [loadingRec, setLoadingRec] = useState(true);
  const [showRecForm, setShowRecForm] = useState(false);
  const [editingRec, setEditingRec] = useState(null);
  const [recForm, setRecForm] = useState(emptyRecAd);
  const [submittingRec, setSubmittingRec] = useState(false);

  const authHeader = useCallback(() => ({
    Authorization: `Bearer ${token}`,
  }), [token]);

  const fetchAds = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/ads`, { headers: authHeader() });
      if (!res.ok) throw new Error(`failed: ${res.status}`);
      const data = await res.json();
      setAds(data.ads || []);
    } catch (err) {
      console.error(err);
      toast.error("광고 목록을 불러오지 못했습니다.");
    } finally {
      setLoading(false);
    }
  }, [token, authHeader]);

  const fetchRecAds = useCallback(async () => {
    if (!token) return;
    setLoadingRec(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/recommended-ads`, { headers: authHeader() });
      if (!res.ok) throw new Error(`failed: ${res.status}`);
      const data = await res.json();
      setRecAds(data.ads || []);
    } catch (err) {
      console.error(err);
      toast.error("추천 광고 목록을 불러오지 못했습니다.");
    } finally {
      setLoadingRec(false);
    }
  }, [token, authHeader]);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated || user?.role !== "admin") {
      navigate("/");
      return;
    }
    fetchAds();
    fetchRecAds();
  }, [authLoading, isAuthenticated, user?.role, navigate, fetchAds, fetchRecAds]);

  // ===== 추천 광고 CRUD =====
  const openCreateRec = () => {
    setEditingRec(null);
    setRecForm({ ...emptyRecAd, sort_order: recAds.length });
    setShowRecForm(true);
  };

  const openEditRec = (ad) => {
    setEditingRec(ad);
    setRecForm({ ...emptyRecAd, ...ad });
    setShowRecForm(true);
  };

  const closeRecForm = () => {
    setShowRecForm(false);
    setEditingRec(null);
    setRecForm(emptyRecAd);
  };

  const handleSubmitRec = async (e) => {
    e.preventDefault();
    if (!recForm.tag.trim()) return toast.error("카테고리 태그를 입력해주세요.");
    if (!recForm.title.trim()) return toast.error("제목을 입력해주세요.");

    setSubmittingRec(true);
    try {
      const url = editingRec
        ? `${API_URL}/api/admin/recommended-ads/${editingRec.id}`
        : `${API_URL}/api/admin/recommended-ads`;
      const method = editingRec ? "PATCH" : "POST";
      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json", ...authHeader() },
        body: JSON.stringify(recForm),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "저장에 실패했습니다.");
      }
      toast.success(editingRec ? "추천 광고가 수정되었습니다." : "추천 광고가 등록되었습니다.");
      closeRecForm();
      fetchRecAds();
    } catch (err) {
      console.error(err);
      toast.error(err.message || "저장 중 오류가 발생했습니다.");
    } finally {
      setSubmittingRec(false);
    }
  };

  const toggleActiveRec = async (ad) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/recommended-ads/${ad.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", ...authHeader() },
        body: JSON.stringify({ is_active: !ad.is_active }),
      });
      if (!res.ok) throw new Error("toggle failed");
      toast.success(ad.is_active ? "추천 광고를 숨겼습니다." : "추천 광고를 노출합니다.");
      fetchRecAds();
    } catch (err) {
      console.error(err);
      toast.error("상태 변경에 실패했습니다.");
    }
  };

  const updateSortOrderRec = async (ad, newOrder) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/recommended-ads/${ad.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", ...authHeader() },
        body: JSON.stringify({ sort_order: newOrder }),
      });
      if (!res.ok) throw new Error("sort failed");
      fetchRecAds();
    } catch (err) {
      console.error(err);
      toast.error("순서 변경에 실패했습니다.");
    }
  };

  const handleDeleteRec = async (ad) => {
    if (!window.confirm(`"${ad.title}" 추천 광고를 정말 삭제하시겠습니까?`)) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/recommended-ads/${ad.id}`, {
        method: "DELETE",
        headers: authHeader(),
      });
      if (!res.ok) throw new Error("delete failed");
      toast.success("추천 광고가 삭제되었습니다.");
      fetchRecAds();
    } catch (err) {
      console.error(err);
      toast.error("삭제에 실패했습니다.");
    }
  };

  const openCreate = () => {
    setEditingAd(null);
    setForm({ ...emptyAd, sort_order: ads.length });
    setShowForm(true);
  };

  const openEdit = (ad) => {
    setEditingAd(ad);
    setForm({ ...emptyAd, ...ad });
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingAd(null);
    setForm(emptyAd);
  };

  const handleUpload = async (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("이미지는 10MB 이하만 업로드 가능합니다.");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("file_type", "image");
      const res = await fetch(`${API_URL}/api/upload`, {
        method: "POST",
        body: fd,
      });
      if (!res.ok) throw new Error(`upload failed: ${res.status}`);
      const data = await res.json();
      setForm((prev) => ({ ...prev, image_url: data.url }));
      toast.success("이미지가 업로드되었습니다.");
    } catch (err) {
      console.error(err);
      toast.error("이미지 업로드에 실패했습니다.");
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return toast.error("제목을 입력해주세요.");
    if (!form.image_url) return toast.error("이미지를 업로드해주세요.");

    setSubmitting(true);
    try {
      const url = editingAd
        ? `${API_URL}/api/admin/ads/${editingAd.id}`
        : `${API_URL}/api/admin/ads`;
      const method = editingAd ? "PATCH" : "POST";
      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json", ...authHeader() },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "저장에 실패했습니다.");
      }
      toast.success(editingAd ? "광고가 수정되었습니다." : "광고가 등록되었습니다.");
      closeForm();
      fetchAds();
    } catch (err) {
      console.error(err);
      toast.error(err.message || "저장 중 오류가 발생했습니다.");
    } finally {
      setSubmitting(false);
    }
  };

  const toggleActive = async (ad) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/ads/${ad.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", ...authHeader() },
        body: JSON.stringify({ is_active: !ad.is_active }),
      });
      if (!res.ok) throw new Error("toggle failed");
      toast.success(ad.is_active ? "광고를 숨겼습니다." : "광고를 노출합니다.");
      fetchAds();
    } catch (err) {
      console.error(err);
      toast.error("상태 변경에 실패했습니다.");
    }
  };

  const updateSortOrder = async (ad, newOrder) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/ads/${ad.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", ...authHeader() },
        body: JSON.stringify({ sort_order: newOrder }),
      });
      if (!res.ok) throw new Error("sort failed");
      fetchAds();
    } catch (err) {
      console.error(err);
      toast.error("순서 변경에 실패했습니다.");
    }
  };

  const handleDelete = async (ad) => {
    if (!window.confirm(`"${ad.title}" 광고를 정말 삭제하시겠습니까?`)) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/ads/${ad.id}`, {
        method: "DELETE",
        headers: authHeader(),
      });
      if (!res.ok) throw new Error("delete failed");
      toast.success("광고가 삭제되었습니다.");
      fetchAds();
    } catch (err) {
      console.error(err);
      toast.error("삭제에 실패했습니다.");
    }
  };

  const openApplications = async (ad) => {
    setApplicationsAd(ad);
    setApplications([]);
    setLoadingApps(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/ads/${ad.id}/applications`, {
        headers: authHeader(),
      });
      if (!res.ok) throw new Error("fetch failed");
      const data = await res.json();
      setApplications(data.applications || []);
    } catch (err) {
      toast.error("신청자 목록을 불러오지 못했습니다.");
    } finally {
      setLoadingApps(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-stone-100 pt-20 flex items-center justify-center" data-testid="admin-ads-loading">
        <Loader2 className="w-8 h-8 text-burgundy-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-100 pt-20" data-testid="admin-ads-page">
      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/admin")}
              className="w-10 h-10 rounded-xl bg-white border border-stone-200 flex items-center justify-center hover:bg-stone-50 transition"
              data-testid="back-to-admin"
            >
              <ArrowLeft className="w-5 h-5 text-stone-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-stone-800">사이드바 광고 관리</h1>
              <p className="text-sm text-stone-500">커뮤니티 페이지 사이드바 AD 카드를 등록·관리합니다.</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {activeTab === "event" ? (
              <>
                <Button
                  onClick={async () => {
                    if (!window.confirm("동일 제목의 광고 중복을 자동으로 정리합니다.\n각 제목당 가장 최근(최신순) 1건만 남기고 나머지는 영구 삭제됩니다.\n계속하시겠습니까?")) return;
                    try {
                      const res = await fetch(`${API_URL}/api/admin/ads/dedupe`, {
                        method: "POST",
                        headers: { Authorization: `Bearer ${token}` },
                      });
                      const d = await res.json();
                      if (!res.ok) throw new Error(d.detail || "실패");
                      toast.success(d.message || `${d.deleted}건 삭제 완료`);
                      fetchAds();
                    } catch (e) {
                      toast.error(e.message || "정리 실패");
                    }
                  }}
                  className="bg-amber-500 hover:bg-amber-600 text-white"
                  data-testid="dedupe-ads-btn"
                  title="동일 제목 광고 중복 자동 정리"
                >
                  🧹 중복 정리
                </Button>
                <Button
                  onClick={openCreate}
                  className="bg-burgundy-500 hover:bg-burgundy-600 text-white"
                  data-testid="create-ad-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  새 광고 등록
                </Button>
              </>
            ) : (
              <Button
                onClick={openCreateRec}
                className="bg-burgundy-500 hover:bg-burgundy-600 text-white"
                data-testid="create-rec-ad-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                새 추천 광고 등록
              </Button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="mb-6 flex items-center gap-1 bg-white rounded-xl p-1 border border-stone-200 w-fit" data-testid="ads-tabs">
          <button
            onClick={() => setActiveTab("recommended")}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition ${
              activeTab === "recommended"
                ? "bg-burgundy-500 text-white shadow"
                : "text-stone-600 hover:bg-stone-50"
            }`}
            data-testid="tab-recommended-ads"
          >
            <Megaphone className="w-4 h-4" />
            추천 광고 (사이드바 텍스트 카드)
            <span className={`ml-1 text-[11px] px-1.5 py-0.5 rounded ${activeTab === "recommended" ? "bg-white/20" : "bg-stone-100 text-stone-500"}`}>
              {recAds.length}
            </span>
          </button>
          <button
            onClick={() => setActiveTab("event")}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition ${
              activeTab === "event"
                ? "bg-burgundy-500 text-white shadow"
                : "text-stone-600 hover:bg-stone-50"
            }`}
            data-testid="tab-event-ads"
          >
            <CalendarDays className="w-4 h-4" />
            이벤트 카드 광고 (이미지)
            <span className={`ml-1 text-[11px] px-1.5 py-0.5 rounded ${activeTab === "event" ? "bg-white/20" : "bg-stone-100 text-stone-500"}`}>
              {ads.length}
            </span>
          </button>
        </div>

        {/* Info card */}
        {activeTab === "event" ? (
          <div className="bg-amber-50/60 border border-amber-200 rounded-xl p-4 mb-6 text-sm text-amber-900">
            <p className="font-semibold mb-1">💡 안내 — 이벤트 광고 카드</p>
            <ul className="list-disc list-inside space-y-1 text-amber-800">
              <li>이미지는 가로 400px / 세로 250px 비율을 권장합니다. (10MB 이하)</li>
              <li>"노출"을 끄면 커뮤니티에서 즉시 숨겨집니다. (삭제와는 별개)</li>
              <li>순서 번호(작을수록 위)로 노출 순서를 조정할 수 있습니다.</li>
              <li>등록된 광고가 하나도 없으면 기본 광고 카드가 자동 표시됩니다.</li>
            </ul>
          </div>
        ) : (
          <div className="bg-blue-50/60 border border-blue-200 rounded-xl p-4 mb-6 text-sm text-blue-900">
            <p className="font-semibold mb-1">💡 안내 — 추천 광고 (텍스트 카드)</p>
            <ul className="list-disc list-inside space-y-1 text-blue-800">
              <li>커뮤니티 페이지 우측 사이드바 상단 <strong>"추천 광고"</strong> 영역에 노출됩니다.</li>
              <li>이미지 없이 카테고리 태그 + 제목 + 링크로 구성된 작은 텍스트 카드입니다.</li>
              <li>색상 테마를 골라 카테고리별로 컬러 코딩할 수 있습니다.</li>
              <li>"노출"을 끄면 커뮤니티에서 즉시 숨겨집니다.</li>
              <li>순서 번호(작을수록 위)로 노출 순서를 조정할 수 있습니다.</li>
              <li>등록된 추천 광고가 하나도 없으면 기본 카드(여행/건강식품/골프)가 자동 표시됩니다.</li>
            </ul>
          </div>
        )}

        {activeTab === "event" && (
        <>
        {/* Empty state */}
        {ads.length === 0 ? (
          <div className="bg-white rounded-2xl border border-stone-200 p-12 text-center" data-testid="ads-empty">
            <ImageIcon className="w-12 h-12 text-stone-300 mx-auto mb-3" />
            <p className="text-stone-600 mb-4">등록된 광고가 없습니다.</p>
            <Button onClick={openCreate} className="bg-burgundy-500 hover:bg-burgundy-600 text-white">
              <Plus className="w-4 h-4 mr-2" />
              첫 광고 등록하기
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {ads.map((ad) => (
              <div
                key={ad.id}
                className={`bg-white rounded-2xl border overflow-hidden shadow-sm transition ${
                  ad.is_active ? "border-stone-200" : "border-stone-200 opacity-60"
                }`}
                data-testid={`ad-card-${ad.id}`}
              >
                <div className="relative">
                  <img
                    src={resolveImageUrl(ad.image_url)}
                    alt={ad.title}
                    className="w-full h-40 object-cover bg-stone-100"
                  />
                  <div className="absolute top-2 left-2 px-2 py-0.5 bg-gradient-to-r from-amber-400 to-orange-400 text-white rounded-full text-[10px] font-bold tracking-wider uppercase shadow">
                    Upcoming Event
                  </div>
                  {ad.action_type === "event_apply" && (
                    <div className="absolute top-2 right-2 px-2 py-0.5 bg-amber-500 text-white rounded-full text-[10px] font-semibold flex items-center gap-1">
                      <CalendarDays className="w-2.5 h-2.5" />
                      이벤트 신청
                    </div>
                  )}
                  {!ad.is_active && (
                    <div className="absolute inset-0 bg-stone-900/40 flex items-center justify-center">
                      <span className="bg-white/95 text-stone-800 text-xs font-bold px-3 py-1 rounded-full">숨김</span>
                    </div>
                  )}
                </div>
                <div className="p-4">
                  <p className="font-bold text-stone-800 text-sm mb-1 truncate">{ad.title}</p>
                  <p className="text-xs text-stone-500 mb-3 line-clamp-2 min-h-[2rem]">{ad.description || "—"}</p>
                  {ad.event_date && (() => {
                    const today = new Date().toISOString().slice(0, 10);
                    const isPast = ad.event_date < today;
                    return (
                      <p className={`text-[11px] mb-2 flex items-center gap-1 ${isPast ? "text-red-600 font-semibold" : "text-emerald-700"}`}>
                        <CalendarDays className="w-3 h-3" />
                        이벤트일: {ad.event_date}{isPast ? " (만료 — 사이드바에서 자동 숨김됨)" : ""}
                      </p>
                    );
                  })()}
                  {ad.link_url ? (
                    <a
                      href={ad.link_url}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 text-[11px] text-burgundy-500 hover:underline truncate max-w-full mb-3"
                    >
                      <ExternalLink className="w-3 h-3 shrink-0" />
                      <span className="truncate">{ad.link_url}</span>
                    </a>
                  ) : (
                    <p className="text-[11px] text-stone-400 mb-3">링크 없음</p>
                  )}

                  <div className="flex items-center gap-1 mb-3 text-[11px] text-stone-500">
                    <GripVertical className="w-3 h-3" />
                    <span>순서:</span>
                    <input
                      type="number"
                      defaultValue={ad.sort_order ?? 0}
                      onBlur={(e) => {
                        const v = parseInt(e.target.value, 10);
                        if (!Number.isNaN(v) && v !== ad.sort_order) updateSortOrder(ad, v);
                      }}
                      className="w-14 px-2 py-0.5 border border-stone-200 rounded text-stone-700"
                      data-testid={`ad-sort-${ad.id}`}
                    />
                  </div>

                  <div className="flex items-center gap-2">
                    {ad.action_type === "event_apply" && (
                      <button
                        onClick={() => openApplications(ad)}
                        className="inline-flex items-center justify-center gap-1 text-xs font-medium px-2 py-1.5 rounded-lg bg-amber-50 text-amber-700 hover:bg-amber-100 transition"
                        data-testid={`ad-applications-${ad.id}`}
                        title="신청자 보기"
                      >
                        <UsersIcon className="w-3.5 h-3.5" />
                        신청자
                      </button>
                    )}
                    <button
                      onClick={() => toggleActive(ad)}
                      className={`flex-1 inline-flex items-center justify-center gap-1 text-xs font-medium px-3 py-1.5 rounded-lg transition ${
                        ad.is_active
                          ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                          : "bg-stone-100 text-stone-600 hover:bg-stone-200"
                      }`}
                      data-testid={`ad-toggle-${ad.id}`}
                    >
                      {ad.is_active ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                      {ad.is_active ? "노출중" : "숨김"}
                    </button>
                    <button
                      onClick={() => openEdit(ad)}
                      className="px-3 py-1.5 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 transition"
                      data-testid={`ad-edit-${ad.id}`}
                    >
                      <Edit className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => handleDelete(ad)}
                      className="px-3 py-1.5 rounded-lg bg-red-50 text-red-500 hover:bg-red-100 transition"
                      data-testid={`ad-delete-${ad.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        </>
        )}

        {/* ===== 추천 광고 (텍스트 카드) 탭 ===== */}
        {activeTab === "recommended" && (
          loadingRec ? (
            <div className="flex justify-center py-20">
              <Loader2 className="w-8 h-8 text-burgundy-500 animate-spin" />
            </div>
          ) : recAds.length === 0 ? (
            <div className="bg-white rounded-2xl border border-stone-200 p-12 text-center" data-testid="rec-ads-empty">
              <Megaphone className="w-12 h-12 text-stone-300 mx-auto mb-3" />
              <p className="text-stone-600 mb-1">등록된 추천 광고가 없습니다.</p>
              <p className="text-xs text-stone-400 mb-4">현재는 기본 카드(여행·건강식품·골프)가 노출 중입니다.</p>
              <Button onClick={openCreateRec} className="bg-burgundy-500 hover:bg-burgundy-600 text-white">
                <Plus className="w-4 h-4 mr-2" />
                첫 추천 광고 등록하기
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="rec-ads-grid">
              {recAds.map((ad) => {
                const colors = REC_COLOR_TEXT[ad.color_theme] || REC_COLOR_TEXT.blue;
                return (
                  <div
                    key={ad.id}
                    className={`bg-white rounded-2xl border overflow-hidden shadow-sm transition p-4 ${
                      ad.is_active ? "border-stone-200" : "border-stone-200 opacity-60"
                    }`}
                    data-testid={`rec-ad-card-${ad.id}`}
                  >
                    {/* 실제 사이드바와 동일한 미리보기 */}
                    <div className="rounded-xl p-3 bg-stone-50 border border-stone-100 mb-3">
                      <div className={`text-[10px] font-bold tracking-wider uppercase mb-1 ${colors.tag}`}>{ad.tag}</div>
                      <div className={`text-sm font-semibold ${colors.title} line-clamp-2`}>{ad.title}</div>
                      <div className="text-[11px] text-stone-400 mt-1">스폰서 · 자세히 보기</div>
                    </div>

                    {ad.link_url ? (
                      <a
                        href={ad.link_url}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-[11px] text-burgundy-500 hover:underline truncate max-w-full mb-3"
                      >
                        <ExternalLink className="w-3 h-3 shrink-0" />
                        <span className="truncate">{ad.link_url}</span>
                      </a>
                    ) : (
                      <p className="text-[11px] text-stone-400 mb-3">링크 없음</p>
                    )}

                    <div className="flex items-center gap-1 mb-3 text-[11px] text-stone-500">
                      <GripVertical className="w-3 h-3" />
                      <span>순서:</span>
                      <input
                        type="number"
                        defaultValue={ad.sort_order ?? 0}
                        onBlur={(e) => {
                          const v = parseInt(e.target.value, 10);
                          if (!Number.isNaN(v) && v !== ad.sort_order) updateSortOrderRec(ad, v);
                        }}
                        className="w-14 px-2 py-0.5 border border-stone-200 rounded text-stone-700"
                        data-testid={`rec-ad-sort-${ad.id}`}
                      />
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => toggleActiveRec(ad)}
                        className={`flex-1 inline-flex items-center justify-center gap-1 text-xs font-medium px-3 py-1.5 rounded-lg transition ${
                          ad.is_active
                            ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                            : "bg-stone-100 text-stone-600 hover:bg-stone-200"
                        }`}
                        data-testid={`rec-ad-toggle-${ad.id}`}
                      >
                        {ad.is_active ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                        {ad.is_active ? "노출중" : "숨김"}
                      </button>
                      <button
                        onClick={() => openEditRec(ad)}
                        className="px-3 py-1.5 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 transition"
                        data-testid={`rec-ad-edit-${ad.id}`}
                      >
                        <Edit className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => handleDeleteRec(ad)}
                        className="px-3 py-1.5 rounded-lg bg-red-50 text-red-500 hover:bg-red-100 transition"
                        data-testid={`rec-ad-delete-${ad.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )
        )}
      </div>

      {/* Create / Edit Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50" onClick={closeForm}>
          <form
            onClick={(e) => e.stopPropagation()}
            onSubmit={handleSubmit}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
            data-testid="ad-form-modal"
          >
            <div className="px-6 py-4 border-b border-stone-100 flex items-center justify-between sticky top-0 bg-white">
              <h2 className="text-lg font-bold text-stone-800">
                {editingAd ? "광고 수정" : "새 광고 등록"}
              </h2>
              <button type="button" onClick={closeForm} className="text-stone-400 hover:text-stone-700">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="px-6 py-5 space-y-4">
              {/* Image upload */}
              <div>
                <label className="text-sm font-semibold text-stone-700 mb-2 block">광고 이미지 *</label>
                {form.image_url ? (
                  <div className="relative rounded-xl overflow-hidden border border-stone-200">
                    <img src={resolveImageUrl(form.image_url)} alt="preview" className="w-full h-44 object-cover" />
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, image_url: "" })}
                      className="absolute top-2 right-2 w-8 h-8 rounded-full bg-white/95 flex items-center justify-center hover:bg-white"
                      data-testid="ad-image-remove"
                    >
                      <X className="w-4 h-4 text-stone-700" />
                    </button>
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center w-full h-44 border-2 border-dashed border-stone-300 rounded-xl cursor-pointer hover:bg-stone-50 transition">
                    {uploading ? (
                      <Loader2 className="w-8 h-8 text-burgundy-500 animate-spin" />
                    ) : (
                      <>
                        <Upload className="w-8 h-8 text-stone-400 mb-2" />
                        <p className="text-sm text-stone-600 font-medium">이미지 업로드</p>
                        <p className="text-xs text-stone-400 mt-1">JPG / PNG / WebP · 최대 10MB</p>
                      </>
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => handleUpload(e.target.files?.[0])}
                      data-testid="ad-image-input"
                    />
                  </label>
                )}
              </div>

              {/* Title */}
              <div>
                <label className="text-sm font-semibold text-stone-700 mb-1 block">제목 *</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  className="w-full px-3 py-2 border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-burgundy-400"
                  placeholder="예: 제주 럭셔리 커플 여행"
                  data-testid="ad-title-input"
                  required
                />
              </div>

              {/* Description */}
              <div>
                <label className="text-sm font-semibold text-stone-700 mb-1 block">설명</label>
                <input
                  type="text"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="w-full px-3 py-2 border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-burgundy-400"
                  placeholder="예: 특별한 만남, 특별한 여행으로"
                  data-testid="ad-description-input"
                />
              </div>

              {/* Action Type */}
              <div>
                <label className="text-sm font-semibold text-stone-700 mb-2 block">클릭 시 동작</label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setForm({
                      ...form,
                      action_type: "link",
                      cta_label: form.cta_label === "신청하기" ? "자세히 보기" : form.cta_label,
                    })}
                    className={`flex items-center justify-center gap-2 px-3 py-2 rounded-lg border text-sm transition ${
                      form.action_type === "link"
                        ? "bg-burgundy-50 border-burgundy-300 text-burgundy-700 font-semibold"
                        : "bg-white border-stone-200 text-stone-500 hover:bg-stone-50"
                    }`}
                    data-testid="ad-action-link-btn"
                  >
                    <LinkIcon className="w-4 h-4" /> 외부 링크 이동
                  </button>
                  <button
                    type="button"
                    onClick={() => setForm({
                      ...form,
                      action_type: "event_apply",
                      cta_label: form.cta_label === "자세히 보기" ? "신청하기" : form.cta_label,
                    })}
                    className={`flex items-center justify-center gap-2 px-3 py-2 rounded-lg border text-sm transition ${
                      form.action_type === "event_apply"
                        ? "bg-burgundy-50 border-burgundy-300 text-burgundy-700 font-semibold"
                        : "bg-white border-stone-200 text-stone-500 hover:bg-stone-50"
                    }`}
                    data-testid="ad-action-event-btn"
                  >
                    <CalendarDays className="w-4 h-4" /> 이벤트 신청 받기
                  </button>
                </div>
                <p className="text-[11px] text-stone-400 mt-1.5">
                  {form.action_type === "event_apply"
                    ? "클릭 시 이름·연락처 신청 폼 모달이 열립니다. 접수 시 관리자 이메일로 알림이 발송됩니다."
                    : "클릭 시 아래 링크로 새 창에서 이동합니다."}
                </p>
              </div>

              {/* Event details (only event_apply) */}
              {form.action_type === "event_apply" && (
                <div>
                  <label className="text-sm font-semibold text-stone-700 mb-1 block">
                    행사 일정 및 안내사항
                  </label>
                  <textarea
                    value={form.event_details}
                    onChange={(e) => setForm({ ...form, event_details: e.target.value })}
                    rows={4}
                    className="w-full px-3 py-2 border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-burgundy-400 resize-none"
                    placeholder={"예) 일시: 2026년 5월 9일(토) 오전 10시\n장소: 북한산 우이동 입구\n준비물: 등산복, 도시락, 물\n참가비: 5만원\n문의: 010-XXXX-XXXX"}
                    data-testid="ad-event-details-input"
                  />
                  <p className="text-[11px] text-stone-400 mt-1">신청 폼 상단에 표시되는 안내 문구입니다.</p>
                </div>
              )}

              {/* Event date — 만료일 자동 숨김 */}
              <div>
                <label className="text-sm font-semibold text-stone-700 mb-1 block">
                  이벤트 날짜 <span className="text-stone-400 text-xs font-normal">(선택 — 지난 날짜는 사이드바에서 자동 숨김)</span>
                </label>
                <input
                  type="date"
                  value={form.event_date || ""}
                  onChange={(e) => setForm({ ...form, event_date: e.target.value })}
                  className="px-3 py-2 border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-burgundy-400"
                  data-testid="ad-event-date-input"
                />
                <p className="text-[11px] text-stone-400 mt-1">비워두면 무기한 노출됩니다. 날짜 입력 시 해당 날짜 자정 이후 자동 숨김 처리됩니다.</p>
              </div>

              {/* Link */}
              {form.action_type !== "event_apply" && (
                <div>
                  <label className="text-sm font-semibold text-stone-700 mb-1 block">클릭 시 이동 링크</label>
                  <input
                    type="url"
                    value={form.link_url}
                    onChange={(e) => setForm({ ...form, link_url: e.target.value })}
                    className="w-full px-3 py-2 border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-burgundy-400"
                    placeholder="https://example.com (비워두면 클릭 불가)"
                    data-testid="ad-link-input"
                  />
                </div>
              )}

              {/* CTA */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-semibold text-stone-700 mb-1 block">버튼 문구</label>
                  <input
                    type="text"
                    value={form.cta_label}
                    onChange={(e) => setForm({ ...form, cta_label: e.target.value })}
                    className="w-full px-3 py-2 border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-burgundy-400"
                    placeholder="자세히 보기"
                    data-testid="ad-cta-label-input"
                  />
                </div>
                <div>
                  <label className="text-sm font-semibold text-stone-700 mb-1 block">버튼 스타일</label>
                  <select
                    value={form.cta_style}
                    onChange={(e) => setForm({ ...form, cta_style: e.target.value })}
                    className="w-full px-3 py-2 border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-burgundy-400"
                    data-testid="ad-cta-style-select"
                  >
                    <option value="light">라이트(기본)</option>
                    <option value="dark">다크(강조)</option>
                  </select>
                </div>
              </div>

              {/* Active + sort */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-semibold text-stone-700 mb-1 block">노출 순서</label>
                  <input
                    type="number"
                    value={form.sort_order ?? 0}
                    onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value, 10) || 0 })}
                    className="w-full px-3 py-2 border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-burgundy-400"
                    data-testid="ad-sort-input"
                  />
                </div>
                <div className="flex items-end">
                  <label className="flex items-center gap-2 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={form.is_active}
                      onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                      className="w-4 h-4"
                      data-testid="ad-active-checkbox"
                    />
                    <span className="text-sm text-stone-700 font-medium">노출 활성화</span>
                  </label>
                </div>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-stone-100 flex items-center justify-end gap-2 bg-stone-50">
              <Button type="button" variant="outline" onClick={closeForm} disabled={submitting}>
                취소
              </Button>
              <Button
                type="submit"
                disabled={submitting || uploading}
                className="bg-burgundy-500 hover:bg-burgundy-600 text-white"
                data-testid="ad-submit-btn"
              >
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                {editingAd ? "수정 저장" : "광고 등록"}
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* Applications Modal */}
      {applicationsAd && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50" onClick={() => setApplicationsAd(null)}>
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col"
            data-testid="applications-modal"
          >
            <div className="px-6 py-4 border-b border-stone-100 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold text-stone-800 flex items-center gap-2">
                  <ClipboardList className="w-5 h-5 text-burgundy-500" />
                  신청자 목록
                </h2>
                <p className="text-sm text-stone-500 mt-0.5 truncate">{applicationsAd.title}</p>
              </div>
              <button onClick={() => setApplicationsAd(null)} className="text-stone-400 hover:text-stone-700">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="overflow-y-auto px-6 py-4 flex-1">
              {loadingApps ? (
                <div className="flex justify-center py-10">
                  <Loader2 className="w-6 h-6 text-burgundy-500 animate-spin" />
                </div>
              ) : applications.length === 0 ? (
                <div className="text-center py-10 text-stone-400 text-sm">아직 신청자가 없습니다.</div>
              ) : (
                <div className="space-y-2">
                  <p className="text-xs text-stone-500 mb-3">총 {applications.length}명 신청</p>
                  {applications.map((app, idx) => (
                    <div key={app.id} className="border border-stone-200 rounded-xl p-3 hover:bg-stone-50">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-start gap-3 flex-1 min-w-0">
                          <div className="w-7 h-7 rounded-full bg-burgundy-100 text-burgundy-700 flex items-center justify-center text-xs font-bold shrink-0">
                            {applications.length - idx}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-sm text-stone-800">{app.name}</p>
                            <a href={`tel:${app.phone}`} className="text-sm text-burgundy-500 hover:underline">{app.phone}</a>
                            {app.note ? <p className="text-xs text-stone-500 mt-1 whitespace-pre-wrap">{app.note}</p> : null}
                          </div>
                        </div>
                        <div className="text-[11px] text-stone-400 shrink-0">
                          {new Date(app.created_at).toLocaleString("ko-KR")}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="px-6 py-3 border-t border-stone-100 bg-stone-50 flex items-center justify-between">
              <p className="text-[11px] text-stone-400">CSV 내보내기는 곧 추가됩니다.</p>
              <Button variant="outline" onClick={() => setApplicationsAd(null)}>닫기</Button>
            </div>
          </div>
        </div>
      )}

      {/* Recommended Ad Create / Edit Modal */}
      {showRecForm && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50" onClick={closeRecForm}>
          <form
            onClick={(e) => e.stopPropagation()}
            onSubmit={handleSubmitRec}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
            data-testid="rec-ad-form-modal"
          >
            <div className="px-6 py-4 border-b border-stone-100 flex items-center justify-between sticky top-0 bg-white">
              <h2 className="text-lg font-bold text-stone-800">
                {editingRec ? "추천 광고 수정" : "새 추천 광고 등록"}
              </h2>
              <button type="button" onClick={closeRecForm} className="text-stone-400 hover:text-stone-700">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="px-6 py-5 space-y-4">
              {/* 미리보기 */}
              <div>
                <label className="text-xs font-semibold text-stone-500 mb-2 block uppercase tracking-wider">실시간 미리보기</label>
                <div className="rounded-xl p-3 bg-stone-50 border border-stone-200">
                  <div className={`text-[10px] font-bold tracking-wider uppercase mb-1 ${(REC_COLOR_TEXT[recForm.color_theme] || REC_COLOR_TEXT.blue).tag}`}>
                    {recForm.tag || "카테고리"}
                  </div>
                  <div className={`text-sm font-semibold ${(REC_COLOR_TEXT[recForm.color_theme] || REC_COLOR_TEXT.blue).title}`}>
                    {recForm.title || "광고 제목"}
                  </div>
                  <div className="text-[11px] text-stone-400 mt-1">스폰서 · 자세히 보기</div>
                </div>
              </div>

              {/* Tag */}
              <div>
                <label className="text-sm font-semibold text-stone-700 mb-1 block">카테고리 태그 *</label>
                <input
                  type="text"
                  value={recForm.tag}
                  onChange={(e) => setRecForm({ ...recForm, tag: e.target.value })}
                  className="w-full px-3 py-2 border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-burgundy-400"
                  placeholder="예: 여행, 건강식품, 골프"
                  maxLength={20}
                  data-testid="rec-tag-input"
                  required
                />
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {REC_TAG_PRESETS.map((t) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => setRecForm({ ...recForm, tag: t })}
                      className="text-[11px] px-2 py-0.5 rounded-full bg-stone-100 hover:bg-burgundy-50 hover:text-burgundy-700 text-stone-600 transition"
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>

              {/* Title */}
              <div>
                <label className="text-sm font-semibold text-stone-700 mb-1 block">광고 제목 *</label>
                <input
                  type="text"
                  value={recForm.title}
                  onChange={(e) => setRecForm({ ...recForm, title: e.target.value })}
                  className="w-full px-3 py-2 border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-burgundy-400"
                  placeholder="예: 프리미엄 패키지 30% 할인"
                  data-testid="rec-title-input"
                  required
                />
              </div>

              {/* Color theme */}
              <div>
                <label className="text-sm font-semibold text-stone-700 mb-2 block">색상 테마</label>
                <div className="grid grid-cols-3 gap-2">
                  {REC_COLOR_OPTIONS.map((c) => (
                    <button
                      key={c.value}
                      type="button"
                      onClick={() => setRecForm({ ...recForm, color_theme: c.value })}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-xs transition ${
                        recForm.color_theme === c.value
                          ? "border-burgundy-400 bg-burgundy-50 text-burgundy-700 font-semibold"
                          : "border-stone-200 bg-white text-stone-600 hover:bg-stone-50"
                      }`}
                      data-testid={`rec-color-${c.value}`}
                    >
                      <span className={`w-3 h-3 rounded-full ${c.swatch}`} />
                      {c.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Link */}
              <div>
                <label className="text-sm font-semibold text-stone-700 mb-1 block">클릭 시 이동 링크</label>
                <input
                  type="url"
                  value={recForm.link_url}
                  onChange={(e) => setRecForm({ ...recForm, link_url: e.target.value })}
                  className="w-full px-3 py-2 border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-burgundy-400"
                  placeholder="https://example.com (비워두면 클릭 불가)"
                  data-testid="rec-link-input"
                />
              </div>

              {/* Active + sort */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-semibold text-stone-700 mb-1 block">노출 순서</label>
                  <input
                    type="number"
                    value={recForm.sort_order ?? 0}
                    onChange={(e) => setRecForm({ ...recForm, sort_order: parseInt(e.target.value, 10) || 0 })}
                    className="w-full px-3 py-2 border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-burgundy-400"
                    data-testid="rec-sort-input"
                  />
                </div>
                <div className="flex items-end">
                  <label className="flex items-center gap-2 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={recForm.is_active}
                      onChange={(e) => setRecForm({ ...recForm, is_active: e.target.checked })}
                      className="w-4 h-4"
                      data-testid="rec-active-checkbox"
                    />
                    <span className="text-sm text-stone-700 font-medium">노출 활성화</span>
                  </label>
                </div>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-stone-100 flex items-center justify-end gap-2 bg-stone-50">
              <Button type="button" variant="outline" onClick={closeRecForm} disabled={submittingRec}>
                취소
              </Button>
              <Button
                type="submit"
                disabled={submittingRec}
                className="bg-burgundy-500 hover:bg-burgundy-600 text-white"
                data-testid="rec-submit-btn"
              >
                {submittingRec ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                {editingRec ? "수정 저장" : "추천 광고 등록"}
              </Button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
